import type { FC } from 'react';
import { lazy } from 'react';
import { useParams } from 'react-router-dom';
import { useMemoOne } from 'use-memo-one';
import type { InvoicesParamsForShipper } from 'src/apis/capacity-management/types';
import { useCapacityManagementRoot } from 'src/apis/capacity-management/use-capacity-management-root';
import { useMyInvoices } from 'src/apis/capacity-management/use-my-invoices';
import { Card } from 'src/components/data-display/card';
import { Alert } from 'src/components/feedback/alert';
import { PageSpinner } from 'src/components/spinner-container';
import type { Successful } from 'src/hooks/use-axios';
import { useAffiliateInvoices } from 'src/pages/shippers/invoices/detail/use-affiliate-invoices';
import type { ShipperInvoiceRequest } from 'src/pages/shippers/invoices/detail/use-invoice-for-shipper';
import { useInvoiceForShipper } from 'src/pages/shippers/invoices/detail/use-invoice-for-shipper';

const Page = lazy(() => import('./page'));

export const ShipperInvoiceDetail: FC = () => {
  const capacityManagementRoot = useCapacityManagementRoot();
  const params = useMemoOne<InvoicesParamsForShipper>(() => ({ limit: 1 }), []);
  const invoices = useMyInvoices({ capacityManagementRoot, params });

  const { invoiceId } = useParams<{ invoiceId: string }>();
  const invoice = useInvoiceForShipper({ invoices, invoiceId });

  if (invoices.error)
    return (
      <Card>
        <Alert type="warning">
          The Invoices feature can only be used if you are a shipper admin.
        </Alert>
      </Card>
    );

  if (!invoice.response) return <PageSpinner />;

  if (invoice.response.data._links?.affiliateInvoices) {
    return (
      <LoadAffiliatedInvoices
        invoice={invoice}
        affiliateInvoicesUrl={
          invoice.response.data._links.affiliateInvoices.href
        }
      />
    );
  } else {
    return <Page invoice={invoice} />;
  }
};

type LoadAffiliatedInvoicesProps = {
  affiliateInvoicesUrl: string;
  invoice: Successful<ShipperInvoiceRequest>;
};

const LoadAffiliatedInvoices: FC<LoadAffiliatedInvoicesProps> = ({
  invoice,
  affiliateInvoicesUrl,
}) => {
  const affiliateInvoices = useAffiliateInvoices({
    url: affiliateInvoicesUrl,
  });

  if (!affiliateInvoices.response) return <PageSpinner />;

  return <Page invoice={invoice} affiliateInvoices={affiliateInvoices} />;
};
